import { Components, Theme } from '@mui/material'
import { forwardRef } from 'react'
import { Link, LinkProps } from 'react-router-dom'
import { getRowClickStyles } from 'shared/constants/clickableRows'

const RouterLink = forwardRef<
  any,
  Omit<LinkProps, 'to'> & { href: LinkProps['to'] }
>(({ href, ...props }, ref) => <Link ref={ref} to={href} {...props} />)

const SHADOW = '0px 3px 12px 0px rgba(11, 53, 171, 0.16)'
const HOVER_COLOR = '#F1F3FF'
const CONTRAST_TEXT_COLOR = '#FBFCFF'
const DISABLED_BUTTON_COLOR = '#A1A1A1'
const BORDER_RADIUS = '12px'

const overrides: Components<Omit<Theme, 'components'>> = {
  MuiButton: {
    defaultProps: {
      fullWidth: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: '14px',
        '&:disabled': {
          color: DISABLED_BUTTON_COLOR,
          borderColor: DISABLED_BUTTON_COLOR,
        },

        [theme.breakpoints.down('md')]: {
          fontSize: 12,
          padding: theme.spacing(1),
        },
      }),
    },
  },
  MuiLink: {
    defaultProps: {
      // @ts-ignore
      component: RouterLink,
      underline: 'hover',
    },
  },
  MuiTextField: {
    defaultProps: {
      fullWidth: true,
    },
    styleOverrides: {
      root: {
        '& fieldset': {
          borderRadius: BORDER_RADIUS,
        },
      },
    },
  },
  MuiDialog: {
    defaultProps: {
      PaperProps: {
        sx: {
          borderRadius: BORDER_RADIUS,
          padding: '6px',
        },
      },
    },
  },
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundColor: CONTRAST_TEXT_COLOR,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: BORDER_RADIUS,
        boxShadow: SHADOW,
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        '&:hover': {
          backgroundColor: HOVER_COLOR,
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        background: CONTRAST_TEXT_COLOR,
        borderRadius: BORDER_RADIUS,

        '& svg': {
          color: theme.palette.primary.main,
        },
      }),
    },
  },
  MuiDataGrid: {
    styleOverrides: {
      root: ({ theme }: any) => ({
        borderRadius: BORDER_RADIUS,
        backgroundColor: CONTRAST_TEXT_COLOR,
        padding: '4px',
        boxShadow: SHADOW,
        '& .MuiDataGrid-overlay': {
          fontWeight: 600,
          fontSize: '1.25rem',
        },
        '& .MuiDataGrid-columnHeaderTitle': {
          fontWeight: 600,
        },
        '& .MuiDataGrid-columnSeparator': {
          display: 'none',
        },
        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
          outline: 'none',
        },
        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
          {
            outline: 'none',
          },

        '& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-cell.Mui-selected': {
          backgroundColor: 'inherit',
        },

        '& .MuiDataGrid-row:hover': {
          backgroundColor: HOVER_COLOR,
        },

        ...getRowClickStyles(theme.palette.primary.main),

        '& .MuiDataGrid-actionsCell': {
          [theme.breakpoints.down('md')]: {
            width: '100%',
            justifyContent: 'flex-end',
          },
        },
      }),
    },
  },

  MuiTableContainer: {
    styleOverrides: {
      root: {
        background: CONTRAST_TEXT_COLOR,
        borderRadius: BORDER_RADIUS,
        boxShadow: SHADOW,
      },
    },
  },

  MuiSwitch: {
    styleOverrides: {
      root: {
        width: 55,
        height: 26,
        padding: 0,
        margin: 8,
        '& .MuiSwitch-switchBase': {
          padding: 0,
          margin: 2,
          transitionDuration: '300ms',
          '&.Mui-checked': {
            transform: 'translateX(28px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
              backgroundColor: 'primary.main',
              opacity: 1,
              border: 0,
            },
            '&.Mui-disabled': {
              color: '#fff',
              '& + .MuiSwitch-track': {
                opacity: 0.5,
              },
            },
          },
          '&.Mui-disabled .MuiSwitch-thumb': {
            color: '#ccc',
          },
          '&.Mui-disabled + .MuiSwitch-track': {
            opacity: 0.5,
          },
        },
        '& .MuiSwitch-thumb': {
          boxSizing: 'border-box',
          width: 22,
          height: 22,
        },
        '& .MuiSwitch-track': {
          borderRadius: 26 / 2,
          backgroundColor: '#E9E9EA',
          opacity: 1,
          transition: 'background-color 500ms',
        },
      },
    },
  },
  MuiStepConnector: {
    styleOverrides: {
      line: {
        width: '85px',
      },
    },
  },
}

export default overrides
